import type { CanonicalAssetGroupDto, CanonicalDownloadVersion, CrateDto } from '@primetime/shared/apiContracts';
import { discoverYearHitsYears, filterAssetsForView, getAssetsForLane, getCanonicalLaneDescriptors } from '@primetime/shared';
import type { VersionType } from '@primetime/shared';
import type { CatalogTrackRow } from './displayRows';

export type CanonicalViewCrate = CrateDto & { assetCount: number; laneAssetCount: number };

const DOWNLOAD_VERSION_TYPES: Record<CanonicalDownloadVersion, VersionType> = {
  Clean: 'CLEAN',
  Dirty: 'DIRTY',
  'DJ Edit': 'DJ_EDIT'
};

const LATIN_GENRES = new Set(['reggaeton', 'latin pop', 'latin dance-edm', 'cumbia', 'cumbias', 'regional mexicano', 'bachata', 'merengue', 'dembow']);

function slug(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function marketForGenre(genre: string) {
  return LATIN_GENRES.has(genre.toLowerCase()) ? 'LAT' : 'EN';
}

export function dedupeCanonicalAssetGroups(assets: CanonicalAssetGroupDto[]) {
  const seenAssetGroupIds = new Set<string>();
  return assets.filter((asset) => {
    if (seenAssetGroupIds.has(asset.assetGroupId)) return false;
    seenAssetGroupIds.add(asset.assetGroupId);
    return true;
  });
}

export function getCanonicalAssetGroupPreviewUrl(assetGroupId: string) {
  return `/api/preview/asset-groups/${encodeURIComponent(assetGroupId)}`;
}

export function matchesCanonicalTrackQuery(track: CatalogTrackRow, query: string) {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;

  const structuredFields = [
    track.assetGroupId,
    track.artist,
    track.title,
    track.editorName,
    track.editLabel,
    track.genre,
    track.subgenre,
    track.releaseYear,
    track.bpm,
    track.key,
    ...track.versions.flatMap((version) => [
      version.versionType,
      version.versionType.replace(/_/g, ' ')
    ])
  ];

  return structuredFields.some((field) => field !== null && field !== undefined && String(field).toLowerCase().includes(normalizedQuery));
}

export function canonicalAssetGroupToTrackRow(asset: CanonicalAssetGroupDto): CatalogTrackRow {
  const releaseDate = Number.isInteger(asset.releaseYear) ? `${asset.releaseYear}-01-01` : '';
  // The established canonical AssetGroup preview is the legacy Clean preview.
  // Keep this fallback scoped to Clean; Dirty and DJ must provide their own
  // version-specific preview reference before their controls become playable.
  const legacyCleanPreviewUrl = getCanonicalAssetGroupPreviewUrl(asset.assetGroupId);
  return {
    assetGroupId: asset.assetGroupId,
    trackId: asset.assetGroupId,
    rankingPlacements: asset.rankingPlacements,
    releaseYear: asset.releaseYear,
    date: releaseDate,
    title: asset.title,
    artist: asset.artist,
    artistDisplay: asset.artist,
    genre: asset.genre,
    subgenre: asset.subgenre,
    style: asset.subgenre,
    bpm: asset.bpm,
    key: asset.camelotKey,
    editorName: asset.editorName,
    editLabel: asset.editLabel,
    versions: asset.downloadVersions.map((version) => ({
      trackVersionId: version.trackVersionId,
      versionType: DOWNLOAD_VERSION_TYPES[version.downloadVariant],
      availability: version.availability,
      revision: version.revision,
      downloadFilename: version.downloadFilename,
      previewVideoUrl: version.previewVideoUrl || (version.downloadVariant === 'Clean' ? legacyCleanPreviewUrl : null),
      releaseDate,
    })),
    createdAt: releaseDate,
    downloadCount: 0
  };
}

export function buildCanonicalViewCrates(assets: CanonicalAssetGroupDto[]): CanonicalViewCrate[] {
  const crates: CanonicalViewCrate[] = [];
  for (const lane of getCanonicalLaneDescriptors(assets)) {
    const laneAssets = getAssetsForLane(assets, lane.laneId);
    const label = lane.subgenre || lane.genre;
    const laneSlug = slug(lane.laneId);
    const views = [
      { key: 'PRIME_PICKS' as const, label: 'Prime Picks Top 25', targetCount: 25, displayOrder: 10 },
      { key: 'ESSENTIALS' as const, label: 'Essentials Top 50', targetCount: 50, displayOrder: 20 },
      { key: 'TOP_100' as const, label: 'PrimeTime Top 100', targetCount: 100, displayOrder: 30 }
    ];

    for (const view of views) {
      crates.push({
        id: `canonical-${laneSlug}-${view.key.toLowerCase()}`,
        title: `${label} ${view.label}`,
        market: marketForGenre(lane.genre),
        periodLabel: 'canonical',
        crateGroup: lane.genre,
        subCrate: view.key,
        laneKey: lane.laneId,
        laneType: lane.laneType,
        parentLaneKey: lane.parentLaneId,
        laneGenre: lane.genre,
        laneSubgenre: lane.subgenre,
        displayName: `${label} ${view.label}`,
        displayOrder: view.displayOrder,
        bucketKind: 'PRIMARY',
        targetCount: view.targetCount,
        maxCapacity: null,
        isActive: true,
        assetCount: filterAssetsForView(assets, view.key, lane.laneId).length,
        laneAssetCount: laneAssets.length
      });
    }

    const yearHitsYears = discoverYearHitsYears(laneAssets, lane.laneId);
    const yearHitsAssets = filterAssetsForView(assets, 'YEAR_HITS', lane.laneId);
    crates.push({
      id: `canonical-${laneSlug}-year-hits`,
      title: `${label} Year Hits`,
      market: marketForGenre(lane.genre),
      periodLabel: 'canonical',
      crateGroup: lane.genre,
      subCrate: 'YEAR_HITS',
      laneKey: lane.laneId,
      laneType: lane.laneType,
      parentLaneKey: lane.parentLaneId,
      laneGenre: lane.genre,
      laneSubgenre: lane.subgenre,
      displayName: `${label} Year Hits`,
      displayOrder: 40,
      bucketKind: 'YEAR_HITS',
      bucketLabel: 'Year Hits',
      maxCapacity: null,
      isActive: true,
      assetCount: yearHitsAssets.length,
      laneAssetCount: laneAssets.length
    });

    for (const year of yearHitsYears) {
      crates.push({
        id: `canonical-${laneSlug}-year-hits-${year}`,
        title: `${label} ${year} Hits`,
        market: marketForGenre(lane.genre),
        periodLabel: 'canonical',
        crateGroup: lane.genre,
        subCrate: null,
        laneKey: lane.laneId,
        laneType: lane.laneType,
        parentLaneKey: lane.parentLaneId,
        laneGenre: lane.genre,
        laneSubgenre: lane.subgenre,
        displayName: `${label} ${year} Hits`,
        displayOrder: 40,
        bucketKind: 'YEAR_HITS',
        bucketLabel: `${year} Hits`,
        bucketYear: year,
        maxCapacity: null,
        isActive: true,
        assetCount: filterAssetsForView(assets, 'YEAR_HITS', lane.laneId, year).length,
        laneAssetCount: laneAssets.length
      });
    }
  }

  return crates;
}
